import React, { useEffect, useRef, useState } from "react";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5 from "@amcharts/amcharts5";
import { IconTooltip } from "./IconTooltip/IconTooltip";

import styles from "./index.module.scss";
import { sourceImages } from "../sharedComponets/PieChartAm/Children/souceData";
import { getSourceIcon } from "../../utils/getSourceImage";

const ReviewReviewsChart = ({ data, scroll }) => {
	const [tooltipData, setTooltipData] = useState({ visible: false });
	const chartDiv = useRef(null);
	// A tall overlap list needs its own scrollbar to be reachable, but the
	// tooltip is only shown while hovering the bullet — moving the cursor
	// off the icon to reach that scrollbar would normally fire pointerout
	// and hide the tooltip before the mouse ever gets there. Delaying the
	// hide (and cancelling it if the tooltip itself is entered) gives the
	// cursor room to cross from the icon onto the tooltip.
	const hideTimeoutRef = useRef(null);

	const cancelPendingHide = () => {
		if (hideTimeoutRef.current) {
			clearTimeout(hideTimeoutRef.current);
			hideTimeoutRef.current = null;
		}
	};

	const onTooltipHover = (event) => {
		const { target, point } = event;
		if (target && target.dataItem && target.dataItem.dataContext) {
			cancelPendingHide();
			setTooltipData({
				visible: true,
				positionX: point.x,
				positionY: point.y,
				data: target.dataItem.dataContext,
				canvasWidth: event.originalEvent.target.offsetWidth,
				canvasHeight: event.originalEvent.target.offsetHeight,
			});
		}
	};

	const onTooltipBlur = () => {
		cancelPendingHide();
		hideTimeoutRef.current = setTimeout(() => {
			setTooltipData({ visible: false });
		}, 200);
	};

	const onTooltipMouseEnter = () => {
		cancelPendingHide();
	};

	const onTooltipMouseLeave = () => {
		cancelPendingHide();
		setTooltipData({ visible: false });
	};

	useEffect(() => {
		if (chartDiv.current) {
			// Read the theme's primary color from the CSS custom property
			// (set in src/sass/themes/_light.scss) rather than hardcoding a
			// hex value here, so the badge stays in sync if the theme changes.
			const primaryColor =
				getComputedStyle(document.documentElement)
					.getPropertyValue("--button-background-primary")
					.trim() || "#06a7e1";

			let zoomTimeoutId = null;
			const baseInterval = { timeUnit: "hour", count: 1 };
			const root = am5.Root.new(chartDiv.current);

			const chart = root.container.children.push(
				am5xy.XYChart.new(root, {
					panX: false,
					panY: false,
					// wheelX: "zoomX",
					wheelY: "none",
					// pinchZoomX: true,
					pinchZoomY: false,
					paddingLeft: 0,
				}),
			);
			chart.zoomOutButton.set("forceHidden", true);
			chart.get("colors").set("step", 3);

			const yRenderer = am5xy.AxisRendererY.new(root, {
				minGridDistance: 20,
				minorGridEnabled: true,
			});

			const yAxis = chart.yAxes.push(
				am5xy.CategoryAxis.new(root, {
					maxDeviation: 0.3,
					categoryField: "category",
					renderer: yRenderer,
					tooltip: am5.Tooltip.new(root, {}),
				}),
			);

			yRenderer.labels.template.setAll({
				multiLocation: 0.5,
			});

			yRenderer.grid.template.setAll({
				location: 1,
			});

			// Date axis for the x-axis
			const xAxis = chart.xAxes.push(
				am5xy.DateAxis.new(root, {
					renderer: am5xy.AxisRendererX.new(root, {
						strokeOpacity: 0.1,
						minGridDistance: 60,
					}),
					baseInterval,
					maxDeviation: 0.3,
					maxPrecision: 0,
				}),
			);

			xAxis.get("renderer").grid.template.setAll({
				visible: false,
			});

			// Create series
			const series = chart.series.push(
				am5xy.ColumnSeries.new(root, {
					xAxis: xAxis,
					yAxis: yAxis,
					baseAxis: yAxis,
					valueXField: "date", // Use date for x-axis
					categoryYField: "rating", // Use rating for y-axis
					tooltip: am5.Tooltip.new(root, {
						labelText: "{categoryY}: {valueX}",
					}),
				}),
			);

			series.columns.template.setAll({
				height: 0,
				opacity: 0,
			});
			let scrollbarX;
			if (!scroll) {
				scrollbarX = chart.set(
					"scrollbarX",
					am5xy.XYChartScrollbar.new(root, {
						orientation: "horizontal",
						height: 10,
					}),
				);

				chart.bottomAxesContainer.children.push(scrollbarX);
			}

			const circleTemplate = am5.Template.new({});
			series.bullets.push(function (localRoot, sprite, dataItem) {
				const bulletContainer = am5.Container.new(localRoot, {});
				const background = bulletContainer.children.push(
					am5.Rectangle.new(localRoot, {
						fill: "#fff",
						width: 20,
						height: 20,
						cornerRadius: 5,
						centerX: am5.p50,
						centerY: am5.p50,
					}),
				);

				background.events.on("pointerover", onTooltipHover);

				background.events.on("pointerout", onTooltipBlur);

				const circle = bulletContainer.children.push(
					am5.Circle.new(localRoot, { radius: 10 }, circleTemplate),
				);

				const maskCircle = bulletContainer.children.push(
					am5.Circle.new(localRoot, { radius: 10 }),
				);

				const imageContainer = bulletContainer.children.push(
					am5.Container.new(localRoot, {
						mask: maskCircle,
					}),
				);

				const picture = imageContainer.children.push(
					am5.Picture.new(localRoot, {
						templateField: "pictureSettings",
						cors: null,
						centerX: am5.p50,
						centerY: am5.p50,
						width: 20,
						height: 20,
					}),
				);

				// Reviews sharing the exact same date+rating (same
				// createTime/updateTime) are grouped into this one bullet —
				// a "+N" badge shows how many more are stacked here instead
				// of trying to spread them apart visually (which either
				// misrepresents their timestamp or overflows into a
				// neighboring rating row once a cluster gets big).
				const badge = bulletContainer.children.push(
					am5.Label.new(localRoot, {
						text: "{overlapLabel}",
						populateText: true,
						centerX: am5.p50,
						centerY: am5.p50,
						x: 8,
						y: -8,
						fontSize: 9,
						fontWeight: "600",
						fill: am5.color(0xffffff),
						paddingLeft: 3,
						paddingRight: 3,
						paddingTop: 1,
						paddingBottom: 1,
						background: am5.RoundedRectangle.new(localRoot, {
							fill: am5.color(primaryColor),
						}),
					}),
				);
				badge.adapters.add("visible", (visible, target) => {
					const context = target.dataItem && target.dataItem.dataContext;
					return !!(context && context.count > 1);
				});

				// A small stagger for the rare case where two different
				// sources' clusters land on the exact same date+rating (see
				// positionIndex above) — kept tiny since it only separates a
				// handful of cluster points, not one offset per raw review.
				const positionContext = dataItem.dataContext || {};
				const positionOffset = (positionContext.positionIndex || 0) * 14;
				bulletContainer.set("dx", positionOffset);
				bulletContainer.set("dy", positionOffset);

				background.events.on("click", (event) => {
					if (
						event.target &&
						event.target.dataItem &&
						event.target.dataItem.dataContext
					) {
						window.location.href = `/reviews/reviewId=${event.target.dataItem.dataContext.reviewDocId}`;
					}
				});

				return am5.Bullet.new(localRoot, {
					locationX: 1,
					locationY: 0.5,
					sprite: bulletContainer,
				});
			});
			// Group reviews that would visually collide into a single chart
			// point with a "+N" badge, instead of rendering — and having to
			// visually separate — one bullet per review. This covers both
			// exact ties (TripAdvisor/ReviewShake bookings synced with an
			// identical timestamp, gap = 0) and near-ties (e.g. Google
			// reviews posted a few minutes apart, which still overlap once
			// the ~20px bullet covers more time than that at a zoomed-out
			// view). Reviews are walked in date order per rating row; a gap
			// to the previous review under the collision threshold joins the
			// current cluster, a bigger gap starts a new one — so distant,
			// unrelated reviews are never lumped together.
			const baseDatesByItem = data.map((item) =>
				Date.parse(item.updateTime || item.createTime),
			);
			const validBaseDates = baseDatesByItem.filter((d) => !isNaN(d));
			const totalRangeMs =
				validBaseDates.length > 0
					? Math.max(...validBaseDates) - Math.min(...validBaseDates)
					: 0;
			const BULLET_PX_SPACING = 32; // comfortably more than the 20px bullet so near-boundary neighbors don't still visually touch
			const chartWidthPx = chartDiv.current.offsetWidth || 800;
			const collisionThresholdMs =
				totalRangeMs > 0
					? (BULLET_PX_SPACING / chartWidthPx) * totalRangeMs
					: 0;

			// Cluster within (rating, source) — not rating alone — so a
			// TripAdvisor cluster and a booking cluster that happen to share
			// the exact same synced timestamp+rating never get merged into
			// one badge. Merging them would still add up to the right total
			// across the chart, but the count next to any single icon would
			// mix two sources together and no longer match either source's
			// real count.
			const indicesByRatingSource = new Map();
			data.forEach((item, index) => {
				const key = `${item.starRating}_${item.source || ""}`;
				if (!indicesByRatingSource.has(key)) {
					indicesByRatingSource.set(key, []);
				}
				indicesByRatingSource.get(key).push(index);
			});

			const chartData = [];
			indicesByRatingSource.forEach((indices) => {
				const sortedIndices = [...indices].sort(
					(a, b) => baseDatesByItem[a] - baseDatesByItem[b],
				);
				let cluster = [];
				let prevDate = null;

				const flushCluster = () => {
					if (cluster.length === 0) return;
					const leadIndex = cluster[0];
					const item = data[leadIndex];
					const overlapItems = cluster.map((clusterIndex) => {
						const clusterItem = data[clusterIndex];
						return {
							reviewer:
								clusterItem.reviewer && clusterItem.reviewer.displayName
									? clusterItem.reviewer.displayName
									: clusterItem.name || "",
							source: clusterItem.source || "",
							storeCode: clusterItem.storeCode || "",
							locationName: clusterItem.locationName || "",
							reviewDocId: clusterItem.reviewDocId || "",
							rating: clusterItem.starRating,
						};
					});
					chartData.push({
						date: baseDatesByItem[leadIndex],
						rating: item.starRating, // Rating for y-axis
						pictureSettings: { src: getSourceIcon(item.source) },
						locationName: item.locationName || "",
						reviewer:
							item.reviewer && item.reviewer.displayName
								? item.reviewer.displayName
								: item.name || "",
						storeCode: item.storeCode || "",
						reviewDocId: item.reviewDocId || "",
						count: cluster.length,
						overlapLabel:
							cluster.length > 1 ? `+${cluster.length - 1}` : "",
						overlapItems,
					});
					cluster = [];
				};

				sortedIndices.forEach((index) => {
					const date = baseDatesByItem[index];
					if (
						prevDate !== null &&
						!isNaN(date) &&
						date - prevDate <= collisionThresholdMs
					) {
						cluster.push(index);
					} else {
						flushCluster();
						cluster = [index];
					}
					prevDate = date;
				});
				flushCluster();
			});

			// Two clusters from different sources can still land on the
			// exact same date+rating (e.g. TripAdvisor and booking synced at
			// the same instant) — stagger those few points slightly so one
			// badge doesn't sit directly on top of and hide the other. This
			// only ever applies to a handful of cluster-level points per
			// rating row (not one offset per raw review), so it doesn't
			// reintroduce the "many overlapping icons" problem.
			const clustersByPosition = new Map();
			chartData.forEach((point) => {
				const positionKey = `${point.date}_${point.rating}`;
				if (!clustersByPosition.has(positionKey)) {
					clustersByPosition.set(positionKey, []);
				}
				clustersByPosition.get(positionKey).push(point);
			});
			clustersByPosition.forEach((points) => {
				points.forEach((point, positionIndex) => {
					point.positionIndex = positionIndex;
				});
			});

			yAxis.data.setAll(
				[
					{ category: "5", color: "#4CAF50" },
					{ category: "4", color: "#03A9F4" },
					{ category: "3", color: "#ababab" },
					{ category: "2", color: "#FF9800" },
					{ category: "1", color: "#FF5722" },
				].reverse(),
			);
			yAxis.get("renderer").labels.template.setAll({
				fontSize: 16,
				fontWeight: 500,
			});
			yAxis
				.get("renderer")
				.labels.template.adapters.add("text", (value, target) => {
					if (target.dataItem && target.dataItem.dataContext) {
						const ratingLabel = target.dataItem.dataContext.category || "";
						const color = target.dataItem.dataContext.color || "#ff0000";
						return `[${color}]${ratingLabel} ★[/]`;
					}
					return value;
				});

			series.data.setAll(chartData);
			// Make stuff animate on load
			series.appear(1000);
			chart.appear(1000, 100);

			// Zoom the initial view to the most recent 30 reviews. `data` is
			// the raw (unclustered) review list, so this ratio matches the
			// original, confirmed-working logic — chartData can no longer be
			// used here since clustering means chartData.length (distinct
			// badges) isn't the review count anymore.
			const DEFAULT_ZOOM_VALUE = 30;
			if (data.length >= DEFAULT_ZOOM_VALUE) {
				zoomTimeoutId = setTimeout(() => {
					const startValue = 1 - DEFAULT_ZOOM_VALUE / data.length;
					xAxis.zoom(startValue, 1);
					if (scrollbarX) {
						scrollbarX.set("start", startValue);
						scrollbarX.set("end", 1);
					}
				}, 1200);
			}

			return () => {
				if (zoomTimeoutId) {
					clearTimeout(zoomTimeoutId);
				}
				root.dispose();
			};
		}
	}, [data]);

	return (
		<div className={styles.reviewReviewsChart}>
			<div
				ref={chartDiv}
				id="chartdiv"
				className={styles.reviewReviewsChart__chart}></div>
			{tooltipData.visible && (
				<IconTooltip
					{...tooltipData}
					onMouseEnter={onTooltipMouseEnter}
					onMouseLeave={onTooltipMouseLeave}
				/>
			)}
		</div>
	);
};

export default ReviewReviewsChart;
